import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { PageHeader } from 'client/js/util/layout_utils';
import FileBrowser from './file_browser';
import FileEditor from './file_editor';
import ImageLightbox from './image_lightbox';

const Filemanager = ({ baseApiUrl }) => {
  const [viewMode, setViewMode] = useState('single'); // 'single' or 'split'
  const [sharedClipboard, setSharedClipboard] = useState(null); // Shared clipboard state
  const [editingFile, setEditingFile] = useState(null); // File currently being edited
  const [viewingImage, setViewingImage] = useState(null); // Image currently being viewed
  const [currentPath, setCurrentPath] = useState('/'); // Track current path for document title

  // Update document title when path changes
  useEffect(() => {
    if (!editingFile && !viewingImage) {
      const pathDisplay = currentPath === '/' ? 'Root' : currentPath;
      document.title = `Dateimanager: ${pathDisplay} | lima-city`;
    }
  }, [currentPath, editingFile, viewingImage]);

  const viewButtons = (
    <div className="btn-group btn-group-toggle segmented-control" data-toggle="buttons">
      <label className={`btn btn-outline-secondary ${viewMode === 'single' ? 'active' : ''}`}>
        <input
          type="radio"
          name="viewMode"
          value="single"
          checked={viewMode === 'single'}
          onChange={() => setViewMode('single')}
        />
        <i className="fa fa-window-maximize" />
        <span className="ml-2">Einzelansicht</span>
      </label>
      <label className={`btn btn-outline-secondary ${viewMode === 'split' ? 'active' : ''}`}>
        <input
          type="radio"
          name="viewMode"
          value="split"
          checked={viewMode === 'split'}
          onChange={() => setViewMode('split')}
        />
        <i className="fa fa-columns" />
        <span className="ml-2">Split-View</span>
      </label>
    </div>
  );

  return (
    <>
      {!editingFile && <PageHeader text="Dateimanager" buttons={viewButtons} />}

      <div className={`filemanager-container ${viewMode}`} style={{ display: editingFile ? 'none' : undefined }}>
        {viewMode === 'single' ? (
          <FileBrowser
            paneId="main"
            sharedClipboard={sharedClipboard}
            setSharedClipboard={setSharedClipboard}
            baseApiUrl={baseApiUrl}
            onEditFile={setEditingFile}
            onViewImage={setViewingImage}
            onPathChange={setCurrentPath}
          />
        ) : (
          <div
            className="split-panes"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px'
            }}
          >
            <FileBrowser
              paneId="left"
              className="pane-left"
              sharedClipboard={sharedClipboard}
              setSharedClipboard={setSharedClipboard}
              baseApiUrl={baseApiUrl}
              onEditFile={setEditingFile}
              onViewImage={setViewingImage}
              onPathChange={setCurrentPath}
            />
            <FileBrowser
              paneId="right"
              className="pane-right"
              sharedClipboard={sharedClipboard}
              setSharedClipboard={setSharedClipboard}
              baseApiUrl={baseApiUrl}
              onEditFile={setEditingFile}
              onViewImage={setViewingImage}
            />
          </div>
        )}
      </div>

      {editingFile && (
        <FileEditor
          file={editingFile}
          onClose={() => setEditingFile(null)}
          baseApiUrl={baseApiUrl}
        />
      )}

      {viewingImage && (
        <ImageLightbox
          file={viewingImage}
          onClose={() => setViewingImage(null)}
          baseApiUrl={baseApiUrl}
        />
      )}
    </>
  );
};

Filemanager.propTypes = {
  baseApiUrl: PropTypes.string,
};

export default Filemanager;